import React, { useState, useEffect, Fragment, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Dialog, Card, CardHeader, CardContent, Radio, RadioGroup, FormControlLabel, Fade, IconButton } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { Button, colors, Typography, TextInput, Loader } from 'kangarootime-ui';
import moment from 'moment';
import DatePickerInput from '../../../../components/TimeOffRequestDialog/DatePickerInput';
import MultiItemSelect from '../../../Messages/components/MultiItemSelect';
import Select from '../../../../components/Select';

const styles = theme => ({
  radio: {
    '&$checked': {
      color: colors.brand_3
    }
  },
  label: {
    marginLeft: 0
  },
  checked: {}
});

const NINE_AM_TODAY = moment().startOf('day').hours(9).minutes(0).second(0).millisecond(0);
const FIVE_PM_TODAY = moment().startOf('day').hours(17).minutes(0).second(0).millisecond(0);
const INITIAL_REQUEST = {
  employee: [],
  startDateTime: NINE_AM_TODAY,
  endDateTime: FIVE_PM_TODAY,
  requestType: 'allDay', // partialDay or allDay
  timeOffType: 'PTO', // PTO, Unpaid, Sick
  description: ''
};

const NewTimeOffRequestForEmployeeByManager = ({
  isOpen,
  onClose,
  onSubmit,
  performingAction,
  ...props
}) => {
  const [calendarSelectedStartDate, setCalendarSelectedStartDate] = useState(moment());
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [timeOffRequestDetails, setTimeOffRequestDetails] = useState({ ...INITIAL_REQUEST });

  useEffect(() => {
    // reset start and end to 9am-5pm when toggled back to 'All Day' request
    if (timeOffRequestDetails.requestType === 'allDay' || timeOffRequestDetails.requestType === 'multiDay') {
      setTimeOffRequestDetails({
        ...timeOffRequestDetails,
        startDateTime: moment(calendarSelectedStartDate).startOf('day').hours(9).minutes(0).second(0).millisecond(0),
        endDateTime: moment(calendarSelectedStartDate).startOf('day').hours(17).minutes(0).second(0).millisecond(0)
      });
    }
  }, [timeOffRequestDetails.requestType]);

  // ensure start and end date time in state have the same date as the selected calendar date
  useEffect(() => {
    if (!moment(calendarSelectedStartDate).isSame(moment(timeOffRequestDetails.startDateTime), 'date')) {
      setTimeOffRequestDetails({
        ...timeOffRequestDetails,
        startDateTime: moment(timeOffRequestDetails.startDateTime).date(moment(calendarSelectedStartDate).date())
      });
    }
  }, [calendarSelectedStartDate, timeOffRequestDetails.startDateTime]);

  const filteredEmployees = props.employees.filter(emp => emp.name.toLowerCase().includes(employeeFilter));
  const handleEmployeeFilter = useCallback(filter => setEmployeeFilter(filter), []);

  const handleInput = useCallback((name, value) => {
    setTimeOffRequestDetails(details => ({
      ...details,
      [name]: value
    }));
  }, []);

  /**
   * `momentTimes` - array of moment objects
   * `formmatedTimes` - array of the string representation of `momentTimes`
   * `reactSelectArr` - array in a format for react-select
   */
  // eslint-disable-next-line
  const [momentTimes, formattedTimes, reactSelectArr] = useMemo(() => {
    // generate moment-objects based on the selected date from 6am-9pm
    let times = [];
    let formattedTimes = [];
    let reactSelectTimesArr = [];
    // limit time input options from 6am-9pm
    let start = moment(calendarSelectedStartDate).startOf('day').hours(6).minutes(0).second(0).millisecond(0);
    const end = moment(calendarSelectedStartDate).startOf('day').hours(21).minute(15).second(0).millisecond(0);

    while (start < end) {
      const _start = start;
      times.push(_start.clone());
      formattedTimes.push(_start.clone().format('h:mm A'));
      start = _start.add('m', 15);
    }

    reactSelectTimesArr = formattedTimes.map((time, index) => ({
      label: time,
      value: times[index]
    }));

    return [times, formattedTimes, reactSelectTimesArr];
  }, [calendarSelectedStartDate]);

  const validateRequest = useCallback(() => {
    return (
      moment(timeOffRequestDetails.startDateTime).isBefore(moment(timeOffRequestDetails.endDateTime)) &&
      timeOffRequestDetails.employee.length === 1 &&
      timeOffRequestDetails.description !== ''
    );
  }, [timeOffRequestDetails]);

  const submitRequest = useCallback(() => {
    const request = {
      ...timeOffRequestDetails,
      requestType: timeOffRequestDetails.requestType === 'multiDay' ? 'allDay' : timeOffRequestDetails.requestType,
      employee: timeOffRequestDetails.employee[0]
    };

    onSubmit && onSubmit(request);
  }, [timeOffRequestDetails, onSubmit]);

  const handleClose = useCallback(() => {
    setTimeOffRequestDetails({ ...INITIAL_REQUEST });
    onClose && onClose();
  }, []);

  return (
    <Dialog open={isOpen} onClose={handleClose} disableEnforceFocus>
      <Card style={{ overflow: 'auto' }}>
        <CardHeader
          style={{ backgroundColor: colors.brand_1, color: '#FFFFFF' }}
          title='New Time Off Request'
          action={
            <IconButton aria-label='Close' onClick={handleClose}>
              <div className='icon-toolbar-icon'>
                <i className='fa fa-times' />
              </div>
            </IconButton>
          }
        />
        <CardContent>
          <Typography type='subsectionHeader'>Who would you like to create a time off request for?</Typography>
          <MultiItemSelect
            label='Employee'
            items={props.employees}
            onItemSelect={employee => handleInput('employee', employee)}
            onFilter={handleEmployeeFilter}
            filteredItems={filteredEmployees}
            disableSelection={timeOffRequestDetails.employee && timeOffRequestDetails.employee.length === 1} // only allow one employee to be selected
          />
          <div style={{ marginTop: 16 }}>
            <div className='time-off-request-container'>
              <div className='time-off-inputs'>
                <RadioGroup
                  row
                  value={timeOffRequestDetails.requestType}
                  onChange={event => handleInput('requestType', event.target.value)}
                >
                  <FormControlLabel
                    value='multiDay'
                    classes={{ root: props.classes.label }}
                    control={<Radio classes={{ root: props.classes.radio, checked: props.classes.checked }} />}
                    label='Multiple Days'
                  />
                  <FormControlLabel
                    value='allDay'
                    classes={{ root: props.classes.label }}
                    control={<Radio classes={{ root: props.classes.radio, checked: props.classes.checked }} />}
                    label='All Day'
                  />
                  <FormControlLabel
                    value='partialDay'
                    classes={{ root: props.classes.label }}
                    control={<Radio classes={{ root: props.classes.radio, checked: props.classes.checked }} />}
                    label='Partial Day'
                  />
                </RadioGroup>
                <Select
                  label='Time-off Type'
                  value={{ label: timeOffRequestDetails.timeOffType, value: timeOffRequestDetails.timeOffType }}
                  options={[{ label: 'PTO', value: 'PTO' }, { label: 'Unpaid', value: 'Unpaid' }]}
                  onChange={option => handleInput('timeOffType', option.value)}
                />
                {timeOffRequestDetails.requestType === 'partialDay' &&
                  <Fragment>
                    <Fade in={timeOffRequestDetails.requestType === 'partialDay'}>
                      <div style={{ marginTop: 8 }}>
                        <Select
                          label='Start Time'
                          options={reactSelectArr}
                          value={{ label: moment(timeOffRequestDetails.startDateTime).format('h:mm A'), value: moment(timeOffRequestDetails.startDateTime) }}
                          onChange={option => handleInput('startDateTime', option.value)}
                        />
                      </div>
                    </Fade>
                    <Fade in={timeOffRequestDetails.requestType === 'partialDay'}>
                      <div style={{ marginTop: 8 }}>
                        <Select
                          label='End Time'
                          options={reactSelectArr}
                          value={{ label: moment(timeOffRequestDetails.endDateTime).format('h:mm A'), value: moment(timeOffRequestDetails.endDateTime) }}
                          onChange={option => handleInput('endDateTime', option.value)}
                        />
                      </div>
                    </Fade>
                  </Fragment>
                }
              </div>
              <div className='time-off-calendar'>
                <DatePickerInput
                  disableWeekends
                  disableToday={timeOffRequestDetails.requestType === 'allDay' || timeOffRequestDetails.requestType === 'multiDay'}
                  allowRangeSelect={timeOffRequestDetails.requestType === 'multiDay'}
                  startDatetime={null}
                  endDatetime={null}
                  onDateSelect={({ startDate, endDate }) => {
                    setCalendarSelectedStartDate(startDate);
                    handleInput('startDateTime', startDate);

                    if (endDate.isValid()) {
                      handleInput('endDateTime', endDate);
                    } else {
                      // if end date is not valid (selected the start to a range time off request), default to 5pm of the selected date
                      handleInput('endDateTime', startDate.clone().hours(17).minutes(0).second(0).millisecond(0));
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <TextInput
              label='Description'
              value={timeOffRequestDetails.description}
              onChange={({ target: { value } }) => handleInput('description', value)}
              style={{ width: '100%' }}
            />
          </div>
          <div className='flex-rows' style={{ justifyContent: 'center', marginTop: 8 }}>
            <Loader loading={performingAction}>
              <Button
                disabled={!validateRequest()}
                onClick={submitRequest}
              >
                Submit
              </Button>
            </Loader>
          </div>
        </CardContent>
      </Card>
    </Dialog>
  );
};

NewTimeOffRequestForEmployeeByManager.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  performingAction: PropTypes.bool
};

function mapStateToProps (state) {
  return {
    employees: state.employees.employees
  };
}

export default connect(mapStateToProps)(withStyles(styles)(NewTimeOffRequestForEmployeeByManager));
